import { useEffect } from 'react'
import { faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { Card, Icon, Text, Image, CardColors, Loader } from '@/ui'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { useAnalyticsView } from '@/features/amplitude'
import { selectIsInUnservicedFreeTrial } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'

interface IAccountQuestionsEndScreenProps {
  accounts?: FinancialAccount[]
  transactionsCount: number
  hoursSaved: number
  isFetchingTransactions: boolean
}

export const AccountQuestionsEndScreen: React.FC<
  IAccountQuestionsEndScreenProps
> = ({ accounts, transactionsCount, hoursSaved, isFetchingTransactions }) => {
  const pageView = useAnalyticsView()
  const isInUnservicedFreeTrial = useReselector(selectIsInUnservicedFreeTrial)

  const headerText = isInUnservicedFreeTrial
    ? 'Thanks for connecting your accounts!'
    : 'Our bookkeeping team is getting to work'

  useEffect(() => {
    pageView('payoff')
  }, [pageView])

  return (
    <div className="flex flex-col lg:flex-row gap-8 pt-8">
      <div className="w-full lg:w-1/3 flex justify-center">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/receipt-2-coins.png"
          alt="Receipt with coins"
          className="w-60 object-contain"
        />
      </div>

      <div className="w-full lg:w-2/3 space-y-4">
        <Text variant={TypographyStyles.H2}>{headerText}</Text>

        {isFetchingTransactions ? (
          <div className="flex justify-center py-8">
            <Loader active inline />
          </div>
        ) : (
          <>
            <Text>
              You’ve connected <b>{accounts?.length}</b> accounts with{' '}
              <b>{transactionsCount}</b> transactions.
            </Text>

            <Card color={CardColors.LEVEL_1}>
              <Icon icon={faSparkles} color="text-neutral-500" />

              <Text>
                {hoursSaved > 1 ? (
                  <b>{`You’re saving ${hoursSaved} hours`}</b>
                ) : (
                  <>
                    <b>You&apos;re saving time</b> that you would have spent
                    categorizing your books by yourself.
                  </>
                )}
                {isInUnservicedFreeTrial
                  ? ' This is one of the many ways that Heard can help you manage your finances.'
                  : ' Moving forward, we’ll categorize your transactions for you.'}
              </Text>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}
